// PostgreSQL identifier helpers.
//
// PG identifier rules:
//   * Bare identifiers match `^[A-Za-z_][A-Za-z0-9_]*$` AND must not collide
//     with a reserved word; otherwise PG quotes them with `"…"`.
//   * Unquoted identifiers fold to lower case (e.g. `Users` → `users`).
//   * `"DoubleQuoted"` preserves case verbatim and is a *distinct* identifier
//     from the unquoted lowercased form.
//   * `"` inside the body is escaped by doubling (`""` → `"`).
//
// The resolver prefers bare emission for already-lower-cased identifiers and
// double-quotes anything else (mixed case, contains a non-ident char, or
// matches a reserved word). The reserved-word check is intentionally lax —
// only the small set of PG keywords that are common-enough collisions to
// matter for completion (e.g. `user`, `select`). A miss here just produces
// less-quoted output, never wrong output.
//
// Pure module — no Monaco runtime, no I/O.

const BARE_IDENT_RE = /^[a-z_][a-z0-9_]*$/;
const PG_RESERVED_LOWER: ReadonlySet<string> = new Set([
  'all', 'analyse', 'analyze', 'and', 'any', 'array', 'as', 'asc',
  'asymmetric', 'authorization', 'binary', 'both', 'case', 'cast', 'check',
  'collate', 'collation', 'column', 'concurrently', 'constraint', 'create',
  'cross', 'current_catalog', 'current_date', 'current_role',
  'current_schema', 'current_time', 'current_timestamp', 'current_user',
  'default', 'deferrable', 'desc', 'distinct', 'do', 'else', 'end',
  'except', 'false', 'fetch', 'for', 'foreign', 'freeze', 'from', 'full',
  'grant', 'group', 'having', 'ilike', 'in', 'initially', 'inner',
  'intersect', 'into', 'is', 'isnull', 'join', 'lateral', 'leading',
  'left', 'like', 'limit', 'localtime', 'localtimestamp', 'natural',
  'not', 'notnull', 'null', 'offset', 'on', 'only', 'or', 'order',
  'outer', 'overlaps', 'placing', 'primary', 'references', 'returning',
  'right', 'select', 'session_user', 'similar', 'some', 'symmetric',
  'table', 'tablesample', 'then', 'to', 'trailing', 'true', 'union',
  'unique', 'user', 'using', 'variadic', 'verbose', 'when', 'where',
  'window', 'with',
]);

export function needsDoubleQuote(identifier: string): boolean {
  if (identifier === '') return true;
  // Mixed-case OR contains non-bare characters → must quote to preserve.
  if (!BARE_IDENT_RE.test(identifier)) return true;
  // Reserved PG keyword (lower-case form) → quote so PG doesn't reject it.
  if (PG_RESERVED_LOWER.has(identifier)) return true;
  return false;
}

export function doubleQuote(identifier: string): string {
  return `"${identifier.replace(/"/g, '""')}"`;
}

/** Round-trip-safe PG identifier — quoted only when required. */
export function formatIdentifier(identifier: string): string {
  return needsDoubleQuote(identifier) ? doubleQuote(identifier) : identifier;
}

/** Compose `schema.name` with per-part quote-on-demand. */
export function formatQualifiedTwoPart(schema: string, name: string): string {
  return `${formatIdentifier(schema)}.${formatIdentifier(name)}`;
}

/**
 * Strip the surrounding `"…"` delimiters from a single identifier token,
 * applying the doubled-escape rule. Returns the input verbatim when not
 * quoted — caller is responsible for case-folding bare identifiers if a
 * case-insensitive comparison is required (use `eqIdentifier`).
 */
export function unquoteIdentifier(token: string): string {
  if (token.length < 2) return token;
  const first = token[0]!;
  const last = token[token.length - 1]!;
  if (first === '"' && last === '"') {
    return token.slice(1, -1).replace(/""/g, '"');
  }
  return token;
}

/**
 * PG semantic equality on two identifier tokens.
 *
 * Folding rules:
 *   * Quoted `"…"`  → preserve case (verbatim after unquoting).
 *   * Unquoted      → fold to lower-case.
 *
 * So `Customer` (unquoted, folds to `customer`) equals `customer` (unquoted)
 * but does NOT equal `"Customer"` (quoted, preserves case).
 */
export function eqIdentifier(a: string, b: string): boolean {
  return foldIdentifier(a) === foldIdentifier(b);
}

/** PG identifier folding for case-comparison purposes. */
export function foldIdentifier(token: string): string {
  if (token.length >= 2 && token[0] === '"' && token[token.length - 1] === '"') {
    return token.slice(1, -1).replace(/""/g, '"');
  }
  return token.toLowerCase();
}

/**
 * PostgreSQL keeps `orders` and `"Orders"` as two relations (the schema
 * cache does too). Among same-named-but-for-case candidates the one spelled
 * exactly as the reference folds wins — a bare `orders` names `orders` —
 * rather than whichever the snapshot lists first.
 */
export function preferExactSpelling<T extends { readonly name: string }>(
  candidates: readonly T[],
  folded: string,
): T | undefined {
  return candidates.find((t) => t.name === folded) ?? candidates[0];
}

/** The session search_path as the resolver reads it. */
export type PgSearchPath = {
  /** The schemas, in resolution order (as `SHOW search_path` expands them). */
  readonly schemas: readonly string[];
  /**
   * The path is the session's own — read from the server, or SET earlier in
   * the script — so it is the ONLY way a bare name resolves. When false it is
   * PostgreSQL's default, a guess, and a bare name may still take the only
   * relation of that name anywhere. Same rule as the VQE
   * (`snapshotRelations.ts`, `defaultSchemasExact`) and SQL Server's default
   * schema (`mssqlDefaultSchema.ts`).
   */
  readonly exact: boolean;
};

/**
 * The relation a PostgreSQL reference names (`relationFolded` / `schemaFolded`
 * already folded — `foldIdentifier`, so a delimited part keeps its case): with
 * a schema, that schema's; without one, the first search_path schema that
 * holds it, else — on a guessed path only — the only relation of that name. A
 * relation with no schema (created earlier in the buffer) answers either form.
 * An exact spelling beats a case variant throughout (`preferExactSpelling`); an
 * ambiguous bare name resolves to nothing.
 *
 * A part folded to a spelling with an upper-case letter can only have been
 * delimited (a bare name folds to lower case), so it names that spelling only
 * — `"Orders"` never answers with its sibling `orders`. A lower-case part may
 * still take a case variant: a query binding loses its delimiters
 * (`AliasBinding`), so `FROM "Orders" o` reaches here as `orders`.
 */
export function resolvePgRelation<T extends { readonly name: string; readonly schema?: string }>(
  tables: readonly T[],
  relationFolded: string,
  schemaFolded: string | null,
  path: PgSearchPath,
): T | undefined {
  const named = tables.filter((t) => spellingNames(t.name, relationFolded));
  const inSchema = (s: string) => {
    const hits = named.filter((t) => spellingNames(t.schema ?? '', s));
    const exact = hits.filter((t) => t.schema === s);
    return exact.length > 0 ? exact : hits;
  };
  // A relation with no schema (created earlier in the buffer) has no schema to be outside of.
  const schemaless = preferExactSpelling(named.filter((t) => !t.schema), relationFolded);
  if (schemaFolded) return preferExactSpelling(inSchema(schemaFolded), relationFolded) ?? schemaless;
  for (const s of path.schemas) {
    const hit = preferExactSpelling(inSchema(s), relationFolded);
    if (hit) return hit;
  }
  if (schemaless || path.exact) return schemaless;
  const exactSpelling = named.filter((t) => t.name === relationFolded);
  if (exactSpelling.length > 0) return exactSpelling.length === 1 ? exactSpelling[0] : undefined;
  return named.length === 1 ? named[0] : undefined;
}

/**
 * Whether the catalog spelling `catalog` answers the folded reference part
 * `folded` (see {@link resolvePgRelation}): exactly, or — for a lower-case
 * part — case-insensitively.
 */
function spellingNames(catalog: string, folded: string): boolean {
  if (catalog === folded) return true;
  return folded === folded.toLowerCase() && catalog.toLowerCase() === folded;
}
